export interface FlatStepConfig {
  id: string;
  title: string;
  path: string;
  group: string;
  groupTitle: string;
  /** If true, navigating away from this step will replace the history entry instead of pushing */
  skipHistory?: boolean;
}

export const FLAT_STEP_CONFIGS: FlatStepConfig[] = [
  {
    id: 'overview',
    title: 'Overview',
    path: 'overview',
    group: 'overview',
    groupTitle: 'Overview',
  },
  {
    id: 'assets',
    title: 'Select Assets',
    path: 'assets',
    group: 'assets',
    groupTitle: 'Select Assets',
  },
  {
    id: 'assessment-intro',
    title: 'Introduction',
    path: 'assessment-intro',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'assessment-accounts',
    title: 'Account Details',
    path: 'assessment-accounts',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'assessment-projection',
    title: 'Projection',
    path: 'assessment-projection',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'preferences-death-benefits',
    title: 'Death Benefits',
    path: 'preferences-death-benefits',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'preferences-benefit-structure',
    title: 'Benefit Structure',
    path: 'preferences-benefit-structure',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'quotes-loading',
    title: 'Finding Policies',
    path: 'quotes-loading',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
    skipHistory: true,
  },
  {
    id: 'quotes-selection',
    title: 'Policy Options',
    path: 'quotes-selection',
    group: 'quotes',
    groupTitle: 'Policy Options',
  },
  {
    id: 'quotes-funding',
    title: 'Policy Funding',
    path: 'quotes-funding',
    group: 'quotes',
    groupTitle: 'Policy Options',
  },
  {
    id: 'plan-comparison',
    title: 'Plan Comparison',
    path: 'plan-comparison',
    group: 'plan',
    groupTitle: 'Plan',
  },
];

const CCRC_TYPE_A_EXCLUDED_STEP_IDS = new Set([
  'preferences-death-benefits',
  'preferences-benefit-structure',
  'quotes-loading',
]);

export const CCRC_TYPE_A_FLAT_STEP_CONFIGS: FlatStepConfig[] = FLAT_STEP_CONFIGS.filter(
  step => !CCRC_TYPE_A_EXCLUDED_STEP_IDS.has(step.id),
);

// Genworth ends at the coverage assessment — it never shops, sells, or selects policies, so
// the preference, quote, and plan-comparison steps are dropped from the flow and the header.
const GENWORTH_STEP_IDS = new Set([
  'overview',
  'assets',
  'assessment-intro',
  'assessment-accounts',
  'assessment-projection',
]);

export type GuidedPlanningVariant = 'insurance' | 'ccrc-type-a';

export const STEP_GROUPS = [...new Set(FLAT_STEP_CONFIGS.map(s => s.group))];

/** Preference questions in wizard order. */
export const PREFERENCE_WIZARD_QUESTION_STEP_IDS: readonly string[] = [
  'preferences-death-benefits',
  'preferences-benefit-structure',
];

export function getActivePreferenceQuestionStepIds(configs: FlatStepConfig[]): string[] {
  return PREFERENCE_WIZARD_QUESTION_STEP_IDS.filter(id => configs.some(c => c.id === id));
}

export function getFlatStepConfigs(
  variant: GuidedPlanningVariant = 'insurance',
  financialFeasibility = false,
  genworthMode = false,
): FlatStepConfig[] {
  const base = variant === 'ccrc-type-a' ? CCRC_TYPE_A_FLAT_STEP_CONFIGS : FLAT_STEP_CONFIGS;
  const scoped = genworthMode ? base.filter(step => GENWORTH_STEP_IDS.has(step.id)) : base;

  if (!financialFeasibility) {
    return scoped;
  }

  return scoped.map(step =>
    step.id === 'assessment-projection'
      ? { ...step, title: 'Financial Feasibility', groupTitle: 'Financial Feasibility' }
      : step,
  );
}

export function getGuidedPlanningVariant(flags: FeatureFlags): GuidedPlanningVariant {
  if (flags.ccrcTypeAProducts) return 'ccrc-type-a';
  return 'insurance';
}

export function getCompletionStepIds(configs: FlatStepConfig[]): GuidedPlanningStep[] {
  return configs.filter(s => s.id !== 'quotes-loading').map(s => s.id as GuidedPlanningStep);
}

export function getStepIndex(stepId: string, configs: FlatStepConfig[]): number {
  const index = configs.findIndex(s => s.id === stepId);
  if (index === -1) {
    throw new Error(`Step not found: ${stepId}`);
  }
  return index;
}

export function getStepId(stepIndex: number, configs: FlatStepConfig[]): GuidedPlanningStep {
  const stepConfig = configs[stepIndex];
  if (!stepConfig) {
    throw new Error(`Invalid step index: ${stepIndex}`);
  }
  return stepConfig.id as GuidedPlanningStep;
}
